import { useEffect, useState } from "react";
import { CreditCard, Download, ExternalLink, FileText, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useBillingStatus,
  useBillingSubscription,
  useBillingUsage,
  useCreateBillingCheckout,
  useCreateBillingPortal,
  useBillingProfile,
  useUpdateBillingProfile,
  useBillingInvoices,
  useIssueBillingInvoice,
  useDownloadBillingInvoice,
  useCancelBilling,
} from "@/hooks/use-api";
import { ApiError } from "@/lib/api";
import { formatAbsoluteTime } from "@/lib/formatters";

const PAID_PLANS = [
  { id: "essential", label: "Essential" },
  { id: "growth", label: "Growth" },
  { id: "business", label: "Business" },
] as const;

function formatPlanPrice(amount: number, currency: string): string {
  return `${new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount / 100)}/mo`;
}

export function isAllowedBillingUrl(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl);
    if (url.protocol !== "https:") return false;
    return (
      url.hostname === "checkout.prayingmantis.cloud" ||
      url.hostname === "prayingmantis.cloud" ||
      url.hostname.endsWith(".prayingmantis.cloud") ||
      url.hostname === "paystack.com" ||
      url.hostname.endsWith(".paystack.com") ||
      url.hostname === "app.lemonsqueezy.com" ||
      url.hostname.endsWith(".lemonsqueezy.com") ||
      url.hostname.endsWith(".stripe.com")
    );
  } catch {
    return false;
  }
}

function checkoutReturnUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.set("checkout", "success");
  return url.toString();
}

function isActiveSubscriptionStatus(status: string | undefined): boolean {
  return Boolean(status) && status !== "canceled" && status !== "expired";
}

function BillingDocuments({
  provider,
}: {
  provider: "stripe" | "lemonsqueezy" | "paystack" | undefined;
}) {
  const profileQuery = useBillingProfile();
  const invoicesQuery = useBillingInvoices();
  const updateProfile = useUpdateBillingProfile();
  const issueInvoice = useIssueBillingInvoice();
  const downloadInvoice = useDownloadBillingInvoice();
  const [form, setForm] = useState({
    legal_name: "",
    registration_number: "",
    billing_email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "",
    postal_code: "",
    country_code: "ZA",
  });
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const profile = profileQuery.data?.profile;
    if (!profile) return;
    setForm({
      legal_name: profile.legal_name,
      registration_number: profile.registration_number ?? "",
      billing_email: profile.billing_email,
      address_line1: profile.address_line1,
      address_line2: profile.address_line2 ?? "",
      city: profile.city,
      province: profile.province ?? "",
      postal_code: profile.postal_code,
      country_code: profile.country_code,
    });
  }, [profileQuery.data?.profile]);

  async function saveProfile() {
    setMessage(null);
    try {
      await updateProfile.mutateAsync(form);
      setMessage("Customer bill-to details saved.");
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        setMessage(`Could not save billing details: ${error.message}.`);
      } else if (error instanceof ApiError && error.status === 403) {
        setMessage("You do not have permission to update the billing details.");
      } else {
        setMessage("Could not save billing details. Please try again.");
      }
    }
  }

  async function issue(invoiceId: string) {
    setMessage(null);
    try {
      await issueInvoice.mutateAsync(invoiceId);
      setMessage("Billing document generated.");
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        error.message.toLowerCase().includes("billing profile")
      ) {
        setMessage(
          "Invoice could not be issued. Save the required customer bill-to details first.",
        );
      } else if (error instanceof ApiError && error.status === 403) {
        setMessage("You do not have permission to generate this invoice.");
      } else if (error instanceof ApiError && error.message) {
        setMessage(`Invoice could not be issued: ${error.message}.`);
      } else {
        setMessage("Invoice could not be issued. Please try again.");
      }
    }
  }

  async function download(invoiceId: string) {
    setMessage(null);
    try {
      const result = await downloadInvoice.mutateAsync(invoiceId);
      const bytes = Uint8Array.from(atob(result.content_base64), (char) =>
        char.charCodeAt(0),
      );
      const url = URL.createObjectURL(
        new Blob([bytes], { type: result.content_type }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
      setMessage("Invoice PDF downloaded.");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        setMessage("The issued invoice PDF could not be found.");
      } else if (error instanceof ApiError && error.status === 403) {
        setMessage("You do not have permission to download this invoice.");
      } else {
        setMessage("Invoice PDF could not be downloaded. Please try again.");
      }
    }
  }

  const fields = [
    ["legal_name", "Legal or individual name"],
    ["registration_number", "Registration number (optional)"],
    ["billing_email", "Billing email"],
    ["address_line1", "Address line 1"],
    ["address_line2", "Address line 2 (optional)"],
    ["city", "City"],
    ["province", "Province (optional)"],
    ["postal_code", "Postal code"],
    ["country_code", "Country code"],
  ] as const;

  return (
    <Card>
      <CardContent className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold">Billing documents</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {provider === "lemonsqueezy"
              ? "Lemon Squeezy is the merchant of record and issues its own receipts and invoices. Use Manage Billing above to access them."
              : provider === "paystack"
                ? "Paystack payments create paid invoice drafts. No VAT is charged because the supplier is not VAT registered."
                : "Provider-issued billing documents remain available through Manage Billing."}
          </p>
        </div>

        {provider === "paystack" && (
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium">
                Customer billing details (Bill to)
              </h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Enter the customer receiving the invoice. Supplier details are
                configured separately.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {fields.map(([name, label]) => (
                <div className="space-y-1" key={name}>
                  <Label htmlFor={`billing-${name}`}>{label}</Label>
                  <Input
                    id={`billing-${name}`}
                    value={form[name]}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        [name]: event.target.value,
                      }))
                    }
                  />
                </div>
              ))}
            </div>
            <Button
              onClick={saveProfile}
              disabled={updateProfile.isPending}
              variant="outline"
            >
              {updateProfile.isPending ? "Saving…" : "Save bill-to details"}
            </Button>
          </div>
        )}

        <div className="space-y-2">
          {(invoicesQuery.data?.invoices ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No billing documents yet.
            </p>
          ) : (
            invoicesQuery.data?.invoices.map((invoice) => {
              const originalIssued =
                invoice.document_kind !== "credit_note" ||
                invoicesQuery.data.invoices.some(
                  (candidate) =>
                    candidate.id === invoice.related_document_id &&
                    candidate.status === "issued",
                );
              return (
                <div
                  className="flex items-center justify-between rounded-md border p-3"
                  key={invoice.id}
                >
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {invoice.document_number ??
                        (invoice.document_kind === "credit_note"
                          ? "Credit note draft"
                          : "Invoice draft")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {invoice.description} ·{" "}
                      {formatPlanPrice(invoice.total_amount, invoice.currency).replace(
                        "/mo",
                        "",
                      )}
                    </p>
                    {invoice.pricing_currency === "USD" &&
                      invoice.pricing_amount &&
                      invoice.fx_rate &&
                      invoice.fx_rate_date && (
                        <p className="text-xs text-muted-foreground">
                          USD {formatPlanPrice(
                            invoice.pricing_amount,
                            "USD",
                          ).replace("/mo", "")} at {invoice.fx_rate} ZAR/USD on{" "}
                          {invoice.fx_rate_date}
                        </p>
                      )}
                  </div>
                </div>
                {invoice.status === "draft" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => issue(invoice.id)}
                    disabled={issueInvoice.isPending || !originalIssued}
                    title={
                      originalIssued
                        ? undefined
                        : "Generate the original invoice first"
                    }
                  >
                    {originalIssued ? "Generate" : "Original invoice required"}
                  </Button>
                ) : invoice.status === "issued" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => download(invoice.id)}
                    disabled={downloadInvoice.isPending}
                  >
                    <Download className="mr-1.5 h-4 w-4" />
                    PDF
                  </Button>
                ) : invoice.status === "external" &&
                  invoice.provider_document_url &&
                  isAllowedBillingUrl(invoice.provider_document_url) ? (
                  <a
                    className={buttonVariants({ size: "sm", variant: "outline" })}
                    href={invoice.provider_document_url}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Download className="mr-1.5 h-4 w-4" />
                    Provider PDF
                  </a>
                ) : null}
                </div>
              );
            })
          )}
        </div>
        {message && <p className="text-sm text-muted-foreground">{message}</p>}
      </CardContent>
    </Card>
  );
}

export function BillingTab() {
  const checkoutReturning =
    new URLSearchParams(window.location.search).get("checkout") === "success";
  const subQuery = useBillingSubscription();
  const activeSubscription = isActiveSubscriptionStatus(
    subQuery.data?.subscription?.status,
  )
    ? subQuery.data?.subscription
    : null;
  const statusQuery = useBillingStatus(true);
  const usageQuery = useBillingUsage(3);
  const portalMutation = useCreateBillingPortal();
  const checkoutMutation = useCreateBillingCheckout();
  const cancelMutation = useCancelBilling();
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const subscription = activeSubscription ?? statusQuery.data?.subscription;
  const pendingCheckout = statusQuery.data?.pending_checkout;
  const usage = usageQuery.data?.usage ?? [];

  async function handleManageBilling() {
    const result = await portalMutation.mutateAsync({
      return_url: window.location.href,
    });
    if (!isAllowedBillingUrl(result.url)) {
      throw new Error("Invalid billing portal URL");
    }
    window.location.href = result.url;
  }

  async function handleSubscribe(plan: string) {
    setCheckoutError(null);
    setPendingPlan(plan);
    try {
      const result = await checkoutMutation.mutateAsync({
        plan,
        return_url: checkoutReturnUrl(),
      });
      if (!result.checkout_url && result.subscription) {
        await subQuery.refetch();
        setPendingPlan(null);
        return;
      }
      if (!result.checkout_url) {
        throw new Error("Billing checkout URL was not returned");
      }
      if (!isAllowedBillingUrl(result.checkout_url)) {
        throw new Error("Invalid billing checkout URL");
      }
      // Navigating away to the checkout page — keep the spinner until unload.
      window.location.href = result.checkout_url;
    } catch {
      // The provider initialization can finish after the browser loses the
      // response (for example during a cold start). Recover the persisted,
      // idempotent checkout before asking the customer to retry.
      try {
        const refreshed = await statusQuery.refetch();
        const recovered = refreshed.data?.pending_checkout;
        if (
          recovered?.plan === plan &&
          recovered.checkout_url &&
          isAllowedBillingUrl(recovered.checkout_url)
        ) {
          window.location.href = recovered.checkout_url;
          return;
        }
      } catch {
        // Fall through to the actionable retry message below.
      }
      setCheckoutError("Couldn't start checkout. Please try again.");
      setPendingPlan(null);
    }
  }

  async function handleCancelPaystack() {
    if (
      !window.confirm(
        "Cancel automatic renewal? Access continues through the paid period.",
      )
    ) {
      return;
    }
    await cancelMutation.mutateAsync();
  }

  if (subQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          {subscription ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Subscription</h3>
                  <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Plan:</span>{" "}
                      <span className="font-medium capitalize">
                        {subscription.plan}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>{" "}
                      <span className="font-medium capitalize">
                        {subscription.status}
                      </span>
                    </div>
                    {subscription.billing_provider && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Provider:</span>{" "}
                        <span className="font-medium capitalize">
                          {subscription.billing_provider}
                        </span>
                      </div>
                    )}
                    {subscription.current_period_start &&
                      subscription.current_period_end && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">
                            Period:
                          </span>{" "}
                          <span className="font-medium">
                            {formatAbsoluteTime(
                              subscription.current_period_start,
                            ).split(" ")[0]}{" "}
                            -{" "}
                            {formatAbsoluteTime(
                              subscription.current_period_end,
                            ).split(" ")[0]}
                          </span>
                        </div>
                      )}
                    {statusQuery.data?.paystack_mandate?.next_charge_at &&
                      statusQuery.data.paystack_mandate.status === "active" && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">
                            Next charge:
                          </span>{" "}
                          <span className="font-medium">
                            {
                              formatAbsoluteTime(
                                statusQuery.data.paystack_mandate.next_charge_at,
                              ).split(" ")[0]
                            }
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
              {subscription.billing_provider !== "paystack" && (
                <Button
                  onClick={handleManageBilling}
                  disabled={portalMutation.isPending}
                >
                  <ExternalLink className="mr-1.5 h-4 w-4" />
                  Manage Billing
                </Button>
              )}
              {subscription.billing_provider === "paystack" &&
                statusQuery.data?.paystack_mandate?.status === "active" && (
                  <Button
                    variant="outline"
                    onClick={handleCancelPaystack}
                    disabled={cancelMutation.isPending}
                  >
                    Cancel automatic renewal
                  </Button>
                )}
              {statusQuery.data?.paystack_charges?.find(
                (charge) =>
                  charge.status === "requires_action" && charge.action_url,
              )?.action_url && (
                <a
                  className={buttonVariants({ variant: "outline" })}
                  href={
                    statusQuery.data.paystack_charges.find(
                      (charge) =>
                        charge.status === "requires_action" &&
                        charge.action_url,
                    )!.action_url!
                  }
                  rel="noreferrer"
                >
                  Complete payment
                </a>
              )}
            </div>
          ) : checkoutReturning ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="rounded-full bg-muted p-3">
                <CreditCard className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-sm font-semibold">
                Activating subscription…
              </h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                We are waiting for the payment provider to confirm your
                checkout. This page will update automatically.
              </p>
              {pendingCheckout?.checkout_url && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    if (!isAllowedBillingUrl(pendingCheckout.checkout_url!)) {
                      throw new Error("Invalid billing checkout URL");
                    }
                    window.location.href = pendingCheckout.checkout_url!;
                  }}
                >
                  Return to checkout
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="rounded-full bg-muted p-3">
                <CreditCard className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-sm font-semibold">
                No active subscription
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Subscribe to a plan to unlock full features.
              </p>
              <div className="mt-6 grid w-full max-w-2xl gap-3 md:grid-cols-3">
                {PAID_PLANS.map((plan) => {
                  const isThisPending = pendingPlan === plan.id;
                  const price = statusQuery.data?.plan_catalog?.find(
                    (entry) => entry.id === plan.id,
                  );
                  return (
                    <Button
                      key={plan.id}
                      variant={plan.id === "growth" ? "default" : "outline"}
                      disabled={pendingPlan !== null || !price}
                      onClick={() => handleSubscribe(plan.id)}
                      className="h-auto flex-col gap-1 py-4"
                    >
                      {isThisPending ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Redirecting…
                        </span>
                      ) : (
                        <>
                          <span>{plan.label}</span>
                          <span className="text-xs opacity-80">
                            {price
                              ? formatPlanPrice(price.amount, price.currency)
                              : "Unavailable"}
                          </span>
                        </>
                      )}
                    </Button>
                  );
                })}
              </div>
              {statusQuery.data?.provider === "paystack" &&
                statusQuery.data.price_tax_label && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Monthly prices are listed in USD. At each purchase, the USD
                    price is converted to ZAR using the latest available ECB
                    reference rate and that rate is stored on your invoice.{" "}
                    {statusQuery.data.price_tax_label}.
                    {statusQuery.data.tax_invoice_url && (
                      <>
                        {" "}
                        <a
                          className="underline underline-offset-2"
                          href={statusQuery.data.tax_invoice_url}
                          rel="noreferrer"
                          target="_blank"
                        >
                          Invoices
                        </a>
                      </>
                    )}
                  </p>
                )}
              {checkoutError && (
                <p
                  role="alert"
                  className="mt-3 text-sm text-destructive"
                >
                  {checkoutError}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <BillingDocuments provider={statusQuery.data?.provider} />

      <Card>
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">
            Usage (Last 3 Months)
          </h3>
          {usageQuery.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : usage.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No usage data available.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Endpoints</TableHead>
                  <TableHead className="text-right">Events</TableHead>
                  <TableHead className="text-right">Alerts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.map((record) => (
                  <TableRow key={record.period}>
                    <TableCell className="font-medium">
                      {record.period}
                    </TableCell>
                    <TableCell className="text-right">
                      {record.endpoints.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {record.events.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {record.alerts.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
